import { useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MagicWandIcon, Cross2Icon } from '@radix-ui/react-icons';
import { UnstyledButton, Text } from '@/shared/ui';
import { useSuggestionPicker } from './useSuggestionPicker';
import { SuggestionPanel } from './SuggestionPanel';

interface SuggestionPickerProps {
  kind: 'title' | 'description';
  ariaLabel: string;
  onSelect: (value: string) => void;
  fetchSuggestions: () => Promise<string[]>;
}

const TRIGGER_CLASS =
  'inline-flex h-10 items-center gap-1.5 whitespace-nowrap rounded-(--radius-md) ' +
  'border border-(--border-subtle) bg-(--surface-secondary) px-3 text-(length:--text-meta) ' +
  'text-(--text-secondary) transition-colors hover:bg-(--surface-hover) ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--input-focus-ring)';

const INLINE_OPTION_CLASS =
  'w-full text-left px-(--space-3) py-(--space-2-5) text-(length:--text-sm) leading-relaxed ' +
  'text-(--text-primary) rounded-(--radius-md) hover:bg-(--surface-hover) ' +
  'transition-colors cursor-pointer';

/**
 * Magic-wand trigger that fetches and presents listing copy suggestions.
 * Suggestions render as a floating popover below the trigger, inside the
 * form dialog, overlaying content beneath without pushing it down.
 *
 * @param props - Suggestion kind, trigger aria-label, select and fetch callbacks.
 * @returns The picker trigger and its inline popover panel.
 */
export function SuggestionPicker({
  kind,
  ariaLabel,
  onSelect,
  fetchSuggestions,
}: SuggestionPickerProps) {
  const { t } = useTranslation('features/properties');
  const { open, status, options, openAndLoad, reload, close } = useSuggestionPicker({
    fetchSuggestions,
  });
  const panelRef = useRef<HTMLDivElement>(null);

  const handleSelect = useCallback(
    (value: string) => {
      onSelect(value);
      close();
    },
    [onSelect, close],
  );

  useEffect(() => {
    if (!open) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        close();
      }
    };
    document.addEventListener('keydown', handleEscape, true);
    return () => document.removeEventListener('keydown', handleEscape, true);
  }, [open, close]);

  return (
    <div className="relative flex flex-col" data-suggestion-kind={kind}>
      <UnstyledButton type="button" aria-label={ariaLabel} onClick={openAndLoad} className={TRIGGER_CLASS}>
        <MagicWandIcon width={14} height={14} aria-hidden /> {t('propertyDrawer.suggestions.trigger')}
      </UnstyledButton>

      {open && (
        <div
          ref={panelRef}
          data-testid={`suggestion-inline-panel-${kind}`}
          className="absolute top-full right-0 z-50 mt-(--space-1) w-64 rounded-(--radius-lg) border border-(--border-subtle) bg-(--surface-primary) shadow-[var(--shadow-lg)] overflow-hidden"
        >
          <div className="flex items-center justify-between border-b border-(--border-subtle) px-(--space-3) py-(--space-2)">
            <Text component="span" className="text-(length:--text-xs) font-semibold text-(--text-secondary)">
              {t(kind === 'title'
                ? 'propertyDrawer.suggestions.panelTitleForTitle'
                : 'propertyDrawer.suggestions.panelTitleForDescription')}
            </Text>
            <UnstyledButton
              type="button"
              aria-label={t('propertyDrawer.suggestions.closePanel')}
              onClick={close}
              className="rounded-(--radius-sm) p-(--space-1) text-(--text-tertiary) hover:bg-(--surface-hover) hover:text-(--text-primary) transition-colors"
            >
              <Cross2Icon width={14} height={14} />
            </UnstyledButton>
          </div>
          <SuggestionPanel
            status={status}
            options={options}
            onSelect={handleSelect}
            onReroll={reload}
            optionClassName={INLINE_OPTION_CLASS}
          />
        </div>
      )}
    </div>
  );
}
